import React, { useRef, useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { globalRoutes } from '../data/i18n';
import { MapPin, Globe } from 'lucide-react';

const GlobalMap = () => {
  const { language } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const [activeRoute, setActiveRoute] = useState(null);
  const sectionRef = useRef(null);

  const labels = {
    pt: { title: 'Presença Global', subtitle: 'Conectamos o Brasil ao mundo' },
    en: { title: 'Global Presence', subtitle: 'Connecting Brazil to the world' },
    es: { title: 'Presencia Global', subtitle: 'Conectamos Brasil al mundo' },
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Auto-cycle through routes
  useEffect(() => {
    if (!isVisible) return;
    
    const interval = setInterval(() => {
      setActiveRoute((prev) => {
        if (prev === null) return 0;
        return (prev + 1) % globalRoutes.length;
      });
    }, 2000);

    return () => clearInterval(interval);
  }, [isVisible]);

  return (
    <section ref={sectionRef} className="py-16 md:py-24 bg-[#1a1a1a] relative overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1920&q=80')] bg-cover bg-center opacity-20"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#1a1a1a] via-transparent to-[#1a1a1a]"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Title */}
        <div className={`text-center mb-12 transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <Globe className="w-12 h-12 mx-auto text-[#3ECDA3] mb-4" />
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-2">
            {labels[language].title}
          </h2>
          <p className="text-gray-400">{labels[language].subtitle}</p>
        </div>

        {/* Interactive Map Visualization */}
        <div className="relative h-[400px] md:h-[500px]">
          {/* World map background (simplified SVG representation) */}
          <svg
            viewBox="0 0 1000 500"
            className="absolute inset-0 w-full h-full opacity-30"
            fill="none"
          >
            {/* Simplified world continents */}
            <ellipse cx="500" cy="250" rx="450" ry="200" stroke="#3ECDA3" strokeWidth="1" strokeDasharray="5,5" />
            <ellipse cx="500" cy="250" rx="350" ry="150" stroke="#3ECDA3" strokeWidth="0.5" strokeDasharray="3,3" />
            <ellipse cx="500" cy="250" rx="250" ry="100" stroke="#3ECDA3" strokeWidth="0.5" strokeDasharray="2,2" />
          </svg>

          {/* Route lines */}
          <svg viewBox="0 0 1000 500" className="absolute inset-0 w-full h-full">
            {globalRoutes.map((route, index) => {
              const startX = 300;
              const startY = 350;
              const endX = 400 + index * 100;
              const endY = 100 + (index % 3) * 80;
              const isActive = activeRoute === index;
              
              return (
                <g key={index}>
                  {/* Route path */}
                  <path
                    d={`M ${startX} ${startY} Q ${(startX + endX) / 2} ${Math.min(startY, endY) - 100} ${endX} ${endY}`}
                    stroke={isActive ? '#3ECDA3' : '#3ECDA3'}
                    strokeWidth={isActive ? 3 : 1}
                    fill="none"
                    opacity={isActive ? 1 : 0.3}
                    className="transition-all duration-500"
                    strokeDasharray={isActive ? '0' : '5,5'}
                  />
                  
                  {/* Animated dot on active route */}
                  {isActive && (
                    <circle r="6" fill="#3ECDA3" className="animate-pulse">
                      <animateMotion
                        dur="2s"
                        repeatCount="indefinite"
                        path={`M ${startX} ${startY} Q ${(startX + endX) / 2} ${Math.min(startY, endY) - 100} ${endX} ${endY}`}
                      />
                    </circle>
                  )}
                  
                  {/* Destination point */}
                  <circle
                    cx={endX}
                    cy={endY}
                    r={isActive ? 8 : 5}
                    fill={isActive ? '#3ECDA3' : '#3ECDA380'}
                    className="transition-all duration-300"
                  />
                </g>
              );
            })}
            
            {/* Origin point (Santos) */}
            <circle cx="300" cy="350" r="12" fill="#3ECDA3" className="animate-pulse" />
            <circle cx="300" cy="350" r="20" fill="none" stroke="#3ECDA3" strokeWidth="2" className="animate-ping" style={{ animationDuration: '2s' }} />
          </svg>

          {/* Destination Labels */}
          <div className="absolute inset-0 pointer-events-none">
            {globalRoutes.map((route, index) => (
              <div
                key={index}
                className={`absolute transform -translate-x-1/2 transition-all duration-500 ${
                  activeRoute === index ? 'opacity-100 scale-100' : 'opacity-50 scale-90'
                }`}
                style={{
                  left: `${40 + index * 10}%`,
                  top: `${20 + (index % 3) * 16}%`,
                }}
              >
                <div className="flex items-center gap-2 bg-[#3ECDA3]/20 backdrop-blur-sm px-3 py-1 rounded-full">
                  <MapPin className="w-3 h-3 text-[#3ECDA3]" />
                  <span className="text-white text-xs font-medium">{route.to.name}</span>
                </div>
              </div>
            ))}
            
            {/* Origin Label */}
            <div className="absolute left-[30%] top-[70%] transform -translate-x-1/2">
              <div className="flex items-center gap-2 bg-[#3ECDA3] px-4 py-2 rounded-full">
                <MapPin className="w-4 h-4 text-white" />
                <span className="text-white text-sm font-bold">Santos, Brasil</span>
              </div>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className={`grid grid-cols-2 md:grid-cols-4 gap-6 mt-12 transition-all duration-700 delay-300 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          {[
            { value: '196', label: 'Países' },
            { value: '50+', label: 'Portos' },
            { value: '100+', label: 'Aeroportos' },
            { value: '24/7', label: 'Suporte' },
          ].map((stat, index) => (
            <div key={index} className="text-center">
              <p className="text-3xl md:text-4xl font-bold text-[#3ECDA3]">{stat.value}</p>
              <p className="text-gray-400 text-sm">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default GlobalMap;
